import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Copy, Eye, EyeOff, Loader2, Plus, Trash2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { handleErr } from "./AdminUI";

const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
const Line = ({ label, value, secret, testid }) => {
  const [show, setShow] = useState(!secret);
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      <span className="w-24 shrink-0 text-slate-400">{label}</span>
      <code className="flex-1 min-w-0 truncate bg-[#0A0C10] border border-[#273042] rounded px-2 py-1.5 text-slate-200">{show ? value : "•".repeat(16)}</code>
      {secret && <button type="button" onClick={() => setShow(!show)} className="text-slate-400 hover:text-white" aria-label="Show or hide">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>}
      <button type="button" onClick={() => copy(value)} className="text-slate-400 hover:text-white" aria-label={`Copy ${label}`}><Copy className="w-4 h-4" /></button>
    </div>
  );
};

const OBS = [
  ["Settings → Stream → Service", "Custom…"], ["Server", "the RTMPS server below (or RTMP if your network blocks RTMPS)"], ["Stream key", "the stream key below (keep it secret)"],
  ["Output mode", "Advanced"], ["Encoder", "x264 or NVENC/QuickSync (H.264)"], ["Rate control", "CBR"], ["Bitrate", "1080p: 6000 Kbps · 720p: 4000 Kbps"],
  ["Keyframe interval", "2 s"], ["Profile / tuning", "high · none (zerolatency off)"], ["Audio", "AAC · 128–160 Kbps · 48 kHz stereo"],
  ["Video → Output resolution", "1920×1080 or 1280×720"], ["FPS", "30 (or 25)"],
];

export const ObsGuide = () => {
  const [open, setOpen] = useState(false);
  const text = OBS.map(([k, v]) => `${k}: ${v}`).join("\n");
  return (
    <div className="rounded-lg border border-[#273042] p-3" data-testid="obs-guide">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen(!open)} className="text-xs text-white font-semibold" data-testid="obs-guide-toggle">{open ? "▾" : "▸"} OBS setup — everything your team needs</button>
        <button type="button" onClick={() => copy(text)} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="obs-guide-copy"><Copy className="w-3.5 h-3.5" /> Copy settings</button>
      </div>
      {open && (
        <div className="mt-2 space-y-1 text-[11px]">
          {OBS.map(([k, v]) => <div key={k} className="flex gap-2"><span className="w-44 shrink-0 text-slate-400">{k}</span><span className="text-slate-200">{v}</span></div>)}
          <ol className="list-decimal ml-4 mt-2 text-slate-400 space-y-0.5">
            <li>Create the event here, then press <b className="text-white">Use for this stream</b> and save the Live stream as <b className="text-white">Live</b>.</li>
            <li>Paste the server + stream key into OBS and press <b className="text-white">Start Streaming</b>.</li>
            <li>Members see it on Live TV within ~30 s. Viewers pick quality (Auto/1080p/720p/540p/360p/240p) from the player's gear menu.</li>
          </ol>
        </div>
      )}
    </div>
  );
};

// Create / pick a Vimeo Live event, show OBS ingest details, and fill the stream source.
export function VimeoLivePanel({ title, onUse }) {
  const [events, setEvents] = useState([]);
  const [ingest, setIngest] = useState({});
  const [busy, setBusy] = useState(false);
  const [cap, setCap] = useState(null);
  const [checking, setChecking] = useState(false);
  const load = () => endpoints.adminVimeoLiveList().then((r) => setEvents(r.data?.events || [])).catch(() => {});
  const check = () => { setChecking(true); endpoints.adminVimeoLiveCapability().then((r) => setCap(r.data)).catch(() => {}).finally(() => setChecking(false)); };
  useEffect(() => { load(); check(); }, []);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await endpoints.adminVimeoLiveCreate(title || "B&B KIGALI Live");
      setIngest((m) => ({ ...m, [data.eventId]: data })); onUse(data.embedUrl); load();
      toast.success("Vimeo live event created — copy the server URL and stream key into OBS");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  const showKey = async (ev) => {
    try { const { data } = await endpoints.adminVimeoLiveIngest(ev.eventId); setIngest((m) => ({ ...m, [ev.eventId]: data })); } catch (e) { handleErr(e); }
  };
  const del = async (ev) => { if (!window.confirm(`Delete Vimeo event "${ev.title}"?`)) return; await endpoints.adminVimeoLiveDelete(ev.eventId).catch(handleErr); load(); };
  return (
    <div className="sm:col-span-2 rounded-xl bg-[#0A0C10]/60 border border-[#273042] p-4 space-y-3" data-testid="vimeo-live-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-white font-semibold flex items-center gap-2"><Radio className="w-4 h-4 text-[#E11D2A]" /> Vimeo Live</div>
        <button type="button" onClick={create} disabled={busy || cap?.available === false} data-testid="vimeo-live-create" className="h-9 px-3 rounded-lg bg-[#E11D2A] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New Vimeo live event
        </button>
      </div>
      {cap?.available === false && <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="vimeo-live-unavailable">{cap.detail || "Vimeo is not connected."} Until then you can stream from OBS to YouTube Live and choose the YouTube source here. We check every few hours and text you as soon as Vimeo Live is active.
        <button type="button" onClick={check} disabled={checking} data-testid="vimeo-live-recheck" className="ml-2 underline font-semibold disabled:opacity-50">{checking ? "Checking…" : "I've upgraded — check now"}</button></div>}
      {cap?.available && <div className="text-[11px] text-emerald-300" data-testid="vimeo-live-available">Vimeo Live is available on {cap.account} ({cap.plan}).</div>}
      <ObsGuide />
      {events.length === 0 && <p className="text-xs text-slate-500">No Vimeo events yet. Creating one gives you an RTMP(S) server and stream key for OBS / vMix. Requires Vimeo Advanced, Premium or Enterprise.</p>}
      {events.map((ev) => {
        const ig = ingest[ev.eventId];
        return (
          <div key={ev.eventId} className="rounded-lg border border-[#273042] p-3 space-y-2" data-testid={`vimeo-event-${ev.eventId}`}>
            <div className="flex items-center gap-2">
              <span className="flex-1 min-w-0 text-sm text-white truncate">{ev.title}</span>
              <button type="button" onClick={() => onUse(ev.embedUrl)} data-testid={`vimeo-event-use-${ev.eventId}`} className="text-xs text-[#EF4444] font-semibold">Use for this stream</button>
              {!ig && <button type="button" onClick={() => showKey(ev)} data-testid={`vimeo-event-ingest-${ev.eventId}`} className="text-xs text-slate-300 hover:text-white">OBS details</button>}
              <button type="button" onClick={() => del(ev)} className="text-red-400 hover:text-red-300" aria-label="Delete event"><Trash2 className="w-4 h-4" /></button>
            </div>
            {ig && (
              <div className="space-y-1.5">
                <Line label="Server (RTMPS)" value={ig.rtmpsUrl} testid="vimeo-ingest-rtmps" />
                <Line label="Server (RTMP)" value={ig.rtmpUrl} testid="vimeo-ingest-rtmp" />
                <Line label="Stream key" value={ig.streamKey} secret testid="vimeo-ingest-key" />
                {(ig.rtmpsUrl || ig.rtmpUrl) && <button type="button" data-testid="vimeo-ingest-copy-all" className="text-[11px] text-[#EF4444] font-semibold"
                  onClick={() => copy(`B&B KIGALI live — OBS\nServer: ${ig.rtmpsUrl || ig.rtmpUrl}\nBackup server (RTMP): ${ig.rtmpUrl || "-"}\nStream key: ${ig.streamKey}\n\n${OBS.map(([k, v]) => `${k}: ${v}`).join("\n")}`)}>Copy everything for the OBS team</button>}
                {ig.sessionStatus && <div className="text-[11px] text-slate-500">Status: {ig.sessionStatus}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
